import { useCallback, useEffect, useState } from 'react';

import { FAILED_TO_UPDATE_ENTRY } from '../../constants/index.js';

export interface UseInlineEditResult {
  editing: boolean;
  editName: string;
  editDesc: string;
  isSaving: boolean;
  editError: string | null;
  canSave: boolean;
  setEditName: (value: string) => void;
  setEditDesc: (value: string) => void;
  startEditing: () => void;
  cancelEdit: () => void;
  save: (saveFn: () => Promise<void>) => Promise<void>;
}

export function useInlineEdit(
  name: string,
  description: string,
): UseInlineEditResult {
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(name);
  const [editDesc, setEditDesc] = useState(description);
  const [isSaving, setIsSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  useEffect(() => {
    setEditName(name);
    setEditDesc(description);
  }, [name, description]);

  const startEditing = useCallback(() => setEditing(true), []);

  const cancelEdit = useCallback(() => {
    setEditing(false);
    setEditName(name);
    setEditDesc(description);
    setEditError(null);
  }, [name, description]);

  const save = useCallback(async (saveFn: () => Promise<void>) => {
    setEditError(null);
    setIsSaving(true);
    try {
      await saveFn();
      setEditing(false);
    } catch (err) {
      setEditError(err instanceof Error ? err.message : FAILED_TO_UPDATE_ENTRY);
    } finally {
      setIsSaving(false);
    }
  }, []);

  return {
    editing,
    editName,
    editDesc,
    isSaving,
    editError,
    canSave: !!editName.trim() && !isSaving,
    setEditName,
    setEditDesc,
    startEditing,
    cancelEdit,
    save,
  };
}
